import React, { useContext, useEffect, useMemo } from 'react';
import { useForm, FormProvider, UseFormSetError, FieldValues } from 'react-hook-form';
import classNames from 'classnames';

import useApi, { UseInvalidateEndpoint } from 'common/useApi';
import useFormApiErrors from 'common/useFormApiErrors';
import AuthContext from 'common/auth';

import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';
import PayoutAddresses from 'components/PayoutAddresses';
import PayoutNow from 'components/PayoutNow';

import './WalletSettings.scss';

const WalletSettings = () => {
  const {
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
    mutate,
  } = useApi('post', '/api/me/update-wallet-address');
  const { user, selectedSubaccount } = useContext(AuthContext)!;
  const invalidate = UseInvalidateEndpoint();

  const minThreshold = parseFloat(user?.min_payout_threshold ?? '0.001');
  const maxThreshold = parseFloat(user?.max_payout_threshold ?? '10');

  // Threshold is per-subaccount. Target the picked subaccount, or the only one
  // if the account has a single site; otherwise prompt to pick a site first.
  const subaccounts = user?.subaccounts ?? [];
  const target = useMemo(
    () => selectedSubaccount ?? (subaccounts.length === 1 ? subaccounts[0] : null),
    [selectedSubaccount, subaccounts],
  );

  const formMethods = useForm({
    defaultValues: {
      payout_threshold: target ? parseFloat(target.payout_threshold) : minThreshold,
      payout_frequency: target?.payout_frequency ?? 'manual',
      token: '',
    },
    mode: 'onChange',
  });
  const {
    handleSubmit, register, reset, setValue,
    formState: { isSubmitting, isValid, isDirty },
    setError,
  } = formMethods;

  // Reflect the targeted subaccount's current settings when the selection
  // changes (and clear any entered OTP).
  useEffect(() => {
    reset({
      payout_threshold: target ? parseFloat(target.payout_threshold) : minThreshold,
      payout_frequency: target?.payout_frequency ?? 'manual',
      token: '',
    });
  }, [target?.id]);

  const formDisabled
    = isSubmitting || isLoading || isFetching || !user?.has_device || !target || (!isValid && isDirty);

  const onSubmit = ({
    payout_threshold,
    payout_frequency,
    token,
  }: { payout_threshold: number, payout_frequency: string, token: string }) => {
    mutate({
      payout_threshold,
      payout_frequency,
      token,
      subaccount: target?.id,
    });
  };

  useFormApiErrors(
    isError,
    error,
    setError as unknown as UseFormSetError<FieldValues>,
    isSuccess,
    'Payout Settings updated successfully',
  );

  useEffect(() => {
    if(isSuccess) {
      invalidate('/api/me');
      setValue('token', '');
    }
  }, [isSuccess]);


  return (
    <>
      <h3>
        Payout Settings
        {!user?.has_device
          && <>
            <br />
            <small className='tfa-required'>Two-Factor Authentication is required to configure Payout Settings.</small>
          </>
        }
      </h3>
      <div
        className={classNames({
          box: true,
          table: true,
          isLoading: isLoading || isFetching || isSubmitting,
        })}
      >
        {(isLoading || isFetching || isSubmitting) && <Loading />}
        <PayoutAddresses />
        <div>&nbsp;</div>
        <hr />
        <div>&nbsp;</div>
        {subaccounts.length > 1 && !selectedSubaccount && (
          <p className='subaccount-hint'>
            Select a site from the account picker to set its payout threshold.
          </p>
        )}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel
              field='payout_threshold'
              name={`Your minimum payout threshold (min ${minThreshold} BTC)${target ? ` — ${target.username}` : ''}`}
              className='half'
            >
              <input
                type='number'
                step='0.0001'
                min={minThreshold}
                max={maxThreshold}
                placeholder='Payout Threshold'
                {...register('payout_threshold', {
                  required: "Payout Threshold can't be blank",
                  valueAsNumber: true,
                  min: { value: minThreshold, message: `Minimum is ${minThreshold} BTC` },
                  max: { value: maxThreshold, message: `Maximum is ${maxThreshold} BTC` },
                })}
                disabled={!user?.has_device || !target}
              />
            </FormLabel>
            <FormLabel
              field='payout_frequency'
              name='How often to pay out'
              className='half'
            >
              <select
                {...register('payout_frequency')}
                disabled={!user?.has_device || !target}
              >
                <option value='manual'>Manual only (pay on demand)</option>
                <option value='daily'>Daily</option>
                <option value='weekly'>Weekly</option>
              </select>
            </FormLabel>
            <FormLabel field='token' name='Enter the one-time password generated by your OTP app' className='half'>
              <input
                type='text'
                placeholder='######'
                {...register('token', {
                  minLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  maxLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  required: 'Token can\'t be blank',
                })}
                disabled={!user?.has_device || !target}
              />
            </FormLabel>
            <button
              type='submit'
              className='primary big'
              disabled={formDisabled}
            >
              Save Payout Settings
            </button>
          </form>
        </FormProvider>
      </div>
      <PayoutNow target={target} />
    </>
  );
};

export default WalletSettings;
